// PlayHQ sections for the Sylvania Heights Netball Club Shopify theme.
// No dependencies: this file is served directly from the theme's assets.

const PLAYHQ_CLUB_URL =
  'https://www.playhq.com/netball-australia/org/sylvania-heights-netball-club/a5068db2'

async function shncFetch(base, path) {
  const response = await fetch(`${base}${path}`)
  if (!response.ok) throw new Error(`Request failed: ${response.status}`)
  return response.json()
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[c])
}

function formatDate(date) {
  if (!date) return ''
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long'
  })
}

function formatTime(time) {
  if (!time) return ''
  const [hours, minutes] = time.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`
}

// Club names PlayHQ stores in capitals that are genuine abbreviations and
// should stay that way. Everything else gets sentence case.
const ACRONYMS = new Set(['BRNC', 'RSL', 'SHNC', 'YMCA', 'PCYC', 'OLF', 'SJB'])

// PlayHQ team names carry the season and an age marker: "SYLVANIA 26W JNR 14
// YELLOW". On our own pages the club prefix and season are repeated on every
// line, and capitals are slower to read. Ladders keep the full name, because
// there we sit among other clubs.
//
// To show PlayHQ's names verbatim instead, make this return `name` unchanged.
// keepClub is used on ladders: there we are listed among other clubs, so the
// club name is what identifies us and must stay.
function tidyName(name, keepClub = false) {
  let s = String(name ?? '')
  if (!keepClub) s = s.replace(/^SYLVANIA\s+/i, '')
  s = s
    .replace(/\b\d{2}W\b/gi, '')
    .replace(/\bJNR\b/gi, '')
    .replace(/\bSNR\s*\/?\s*0?/gi, 'Seniors ')
    .replace(/\s+/g, ' ')
    .trim()

  return s
    .split(' ')
    .map((word) => {
      if (ACRONYMS.has(word.toUpperCase())) return word.toUpperCase()
      if (/\d/.test(word)) return word
      return word.toLowerCase().replace(/(^|[(/-])([a-z])/g, (m, before, c) => before + c.toUpperCase())
    })
    .join(' ')
}

// PlayHQ reports six outcomes: WON, LOST, DREW, WON_BY_FORFEIT,
// LOST_BY_FORFEIT and null. Anything that merely tests for 'WON' will label a
// draw, and a win by forfeit, as a loss.
function outcomeLabel(outcome) {
  const value = String(outcome || '')
  if (value.startsWith('WON')) {
    return { text: value.includes('FORFEIT') ? 'Won by forfeit' : 'Won', cls: 'win' }
  }
  if (value.startsWith('DREW') || value.startsWith('DRAW')) {
    return { text: 'Drew', cls: 'draw' }
  }
  if (value.startsWith('LOST')) {
    return { text: value.includes('FORFEIT') ? 'Lost by forfeit' : 'Lost', cls: 'loss' }
  }
  return null
}

// Nearly every game is at the one venue, so naming it once in the header beats
// repeating it on every row. Returns null when they genuinely differ.
function commonVenue(games) {
  const counts = new Map()
  for (const { game } of games) {
    if (game.venue) counts.set(game.venue, (counts.get(game.venue) || 0) + 1)
  }
  if (!counts.size) return null
  const [name, count] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]
  return count / games.length >= 0.8 ? name : null
}

function renderError(root, message) {
  root.innerHTML =
    `<p class="shnc-error">${escapeHtml(message)} ` +
    `<a href="${PLAYHQ_CLUB_URL}">View the draw on PlayHQ</a>.</p>`
}

// Two deployments publish the same shapes at different addresses: the
// SiteGround cron writes static JSON files, the Node service exposes routes
// under /api. Sections default to static; set data-source="api" for the other.
function paths(source) {
  const isStatic = source !== 'api'
  return {
    teams: () => (isStatic ? '/teams.json' : '/api/teams'),
    round: () => (isStatic ? '/round.json' : '/api/round'),
    team: (id) => (isStatic ? `/teams/${id}.json` : `/api/teams/${id}/games`),
    grade: (id) => (isStatic ? `/grades/${id}.json` : `/api/grades/${id}/ladder`)
  }
}

class ShncRound extends HTMLElement {
  async connectedCallback() {
    const base = this.dataset.apiBase
    const path = paths(this.dataset.source)
    this.innerHTML = '<p class="shnc-loading">Loading the draw&hellip;</p>'

    try {
      const { round, games } = await shncFetch(base, path.round())
      if (!games.length) {
        this.innerHTML = '<p class="shnc-empty">No upcoming games are scheduled.</p>'
        return
      }
      this.innerHTML = this.render(round, games)
    } catch {
      renderError(this, 'The draw could not be loaded.')
    }
  }

  render(round, games) {
    // Grouped by day, then by start time: a netball Saturday runs in
    // timeslots, so "who is on at 9:45" is the question being asked.
    const byDate = new Map()
    for (const entry of games) {
      const list = byDate.get(entry.game.date) || []
      list.push(entry)
      byDate.set(entry.game.date, list)
    }

    let html = `<h2 class="shnc-heading">This Round &mdash; ${escapeHtml(round)}</h2>`

    const venue = commonVenue(games)
    if (venue) html += `<p class="shnc-meta">${escapeHtml(venue)}</p>`

    const dates = [...byDate.keys()].sort()
    for (const date of dates) {
      html += `<h3 class="shnc-daybar">${escapeHtml(formatDate(date))}</h3>`

      const bySlot = new Map()
      for (const entry of byDate.get(date)) {
        const key = entry.game.time || ''
        const list = bySlot.get(key) || []
        list.push(entry)
        bySlot.set(key, list)
      }

      // Each slot and its games are wrapped together so alternate groups can
      // be banded, which separates them without adding more borders.
      html += '<div class="shnc-day">'
      for (const slot of [...bySlot.keys()].sort()) {
        html += '<div class="shnc-slotgroup"><div class="shnc-slot">' +
          `<span class="shnc-slot-time">${escapeHtml(formatTime(slot) || 'Time to be confirmed')}</span>` +
          '<span class="shnc-slot-rule"></span></div>'

        html += '<div class="shnc-grid">'
        for (const { team, game } of bySlot.get(slot)) {
          html += game.isBye
            ? '<div class="shnc-card shnc-bye">' +
              `<span class="shnc-card-team">${escapeHtml(tidyName(team.name))}</span>` +
              '<span class="shnc-card-opp">Bye this round</span></div>'
            : '<div class="shnc-card">' +
              `<span class="shnc-card-team">${escapeHtml(tidyName(team.name))}</span>` +
              `<span class="shnc-card-opp"><em>v</em> ${escapeHtml(tidyName(game.opponent))}</span>` +
              (game.court ? `<span class="shnc-card-court">${escapeHtml(game.court)}</span>` : '') +
              '</div>'
        }
        html += '</div></div>'
      }
      html += '</div>'
    }
    return html
  }
}

// Index of every club team, grouped into juniors and seniors where the team
// names make that clear. Each entry links back to the same page with ?team=,
// which <shnc-team> below picks up.
class ShncTeams extends HTMLElement {
  async connectedCallback() {
    const base = this.dataset.apiBase
    const path = paths(this.dataset.source)
    this.innerHTML = '<p class="shnc-loading">Loading teams&hellip;</p>'

    try {
      const { season, teams } = await shncFetch(base, path.teams())
      if (!teams.length) {
        this.innerHTML = '<p class="shnc-empty">No teams are available.</p>'
        return
      }
      this.innerHTML = this.render(season, teams)
    } catch {
      renderError(this, 'The team list could not be loaded.')
    }
  }

  group(teams) {
    const groups = new Map()
    for (const team of teams) {
      const name = String(team.name || '')
      const label = /\bJNR\b/i.test(name)
        ? 'Junior teams'
        : /\bSNR\b/i.test(name)
          ? 'Senior teams'
          : 'Teams'
      const list = groups.get(label) || []
      list.push(team)
      groups.set(label, list)
    }
    // Sensible reading order regardless of the order the API returned.
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }

  // This list is placed on more than one page, and only the tables page has
  // anything that answers a click. Elsewhere a link would reload the page to no
  // effect, which is worse than no link at all, so the cards are plain there.
  //
  // Rather than a setting to keep in step on every page, work it out: link the
  // teams when the page holds a ladder or a results table that follows the
  // ?team= in the URL. A section pinned to one grade or one team in the theme
  // editor ignores ?team=, so it does not count. "Always" and "Never" remain
  // available in the section settings for anything this reads wrongly.
  isClickable() {
    const mode = this.dataset.clickable
    if (mode === 'always') return true
    if (mode === 'never') return false
    if (this.dataset.linkBase) return true

    const follows = (tag, setting) =>
      [...document.querySelectorAll(tag)].some((el) => !el.dataset[setting])
    return follows('shnc-ladder', 'gradeId') || follows('shnc-team', 'teamId')
  }

  render(season, teams) {
    const clickable = this.isClickable()
    const target = this.dataset.linkBase || window.location.pathname
    // Mark whichever team the visitor has picked, so the list shows what the
    // ladder below is showing.
    const selected = new URLSearchParams(window.location.search).get('team')

    let html = '<h2 class="shnc-heading">Our teams</h2>' +
      `<p class="shnc-meta">${escapeHtml(season?.name || '')} &middot; ` +
      `${teams.length} teams</p>`

    for (const [label, list] of this.group(teams)) {
      html += `<h3 class="shnc-daybar">${escapeHtml(label)}</h3><div class="shnc-teamgrid">`
      for (const team of list) {
        const active = clickable && team.id === selected ? ' shnc-teamcard--active' : ''
        const inner =
          `<span class="shnc-teamcard-name">${escapeHtml(tidyName(team.name))}</span>` +
          `<span class="shnc-teamcard-grade">${escapeHtml(team.gradeName || '')}</span>`

        html += clickable
          ? `<a class="shnc-teamcard${active}" href="${escapeHtml(target)}?team=${encodeURIComponent(team.id)}">${inner}</a>`
          : `<div class="shnc-teamcard shnc-teamcard--static">${inner}</div>`
      }
      html += '</div>'
    }
    return html
  }
}

class ShncTeam extends HTMLElement {
  async connectedCallback() {
    this.base = this.dataset.apiBase
    this.path = paths(this.dataset.source)

    // An explicit setting pins the section to one team. Otherwise take the
    // team from the URL, which is how the index links through. With neither,
    // render nothing so the index can stand alone on the same page.
    const fromUrl = new URLSearchParams(window.location.search).get('team')
    const teamId = this.dataset.teamId || fromUrl

    if (!teamId) {
      this.innerHTML = ''
      return
    }

    this.innerHTML = '<p class="shnc-loading">Loading&hellip;</p>'

    try {
      const { team, games } = await shncFetch(this.base, this.path.team(teamId))
      this.innerHTML = this.render(team, games) + this.backLink(fromUrl)
    } catch {
      renderError(this, 'The draw could not be loaded.')
    }
  }

  backLink(fromUrl) {
    return fromUrl
      ? `<p class="shnc-back"><a href="${escapeHtml(window.location.pathname)}">All teams</a></p>`
      : ''
  }

  render(team, games) {
    // Next game first, then results with the most recent at the top.
    //
    // The third group is the awkward one: games still marked upcoming whose
    // date has passed — washed out, or never scored. They are neither a
    // fixture nor a result, so they go last rather than leading the table.
    const today = new Date().toLocaleDateString('en-CA')
    const isFinal = (g) => g.status === 'FINAL'
    const isFuture = (g) => (g.date || '') >= today

    const upcoming = games.filter((g) => !isFinal(g) && isFuture(g))
    const completed = games.filter(isFinal).reverse()
    const unresolved = games.filter((g) => !isFinal(g) && !isFuture(g)).reverse()
    const ordered = [...upcoming, ...completed, ...unresolved]

    const rows = ordered.map((g) => {
      const label = outcomeLabel(g.outcome)
      const result = g.isBye
        ? '<span class="shnc-pill shnc-upc">Bye</span>'
        : label
          ? (g.score ? `<b>${g.score.us}&ndash;${g.score.them}</b> ` : '') +
            `<span class="shnc-pill shnc-${label.cls}">${label.text}</span>`
          // A past date with no score is not upcoming, whatever PlayHQ says.
          : isFuture(g)
            ? '<span class="shnc-pill shnc-upc">Upcoming</span>'
            : '<span class="shnc-pill shnc-upc">No result</span>'

      return `<tr><td>${escapeHtml(g.round || '')}</td><td>${escapeHtml(formatDate(g.date))}</td>` +
        `<td>${escapeHtml(formatTime(g.time))}</td>` +
        `<td>${g.isBye ? '&mdash;' : 'vs ' + escapeHtml(tidyName(g.opponent))}</td>` +
        `<td>${escapeHtml(g.court || '')}</td><td>${result}</td></tr>`
    }).join('')

    // On a page where the ladder above already names the team, a fixed
    // heading like "Results" avoids saying it twice.
    const heading = this.dataset.heading || tidyName(team.name)

    return `<h2 class="shnc-heading">${escapeHtml(heading)}</h2>` +
      `<p class="shnc-meta">Grade: ${escapeHtml(team.gradeName || '')}</p>` +
      '<div class="shnc-scroll"><table class="shnc-table"><thead><tr>' +
      '<th>Rd</th><th>Date</th><th>Time</th><th>Opponent</th><th>Court</th><th>Result</th>' +
      `</tr></thead><tbody>${rows}</tbody></table></div>`
  }
}

class ShncLadder extends HTMLElement {
  async connectedCallback() {
    const base = this.dataset.apiBase
    const path = paths(this.dataset.source)
    this.ourTeamName = this.dataset.ourTeamName || 'Sylvania'

    let gradeId = this.dataset.gradeId
    let heading = null

    // With no grade pinned in the section settings, follow the team the
    // visitor picked from the teams list above.
    if (!gradeId) {
      this.selectedTeamId = new URLSearchParams(window.location.search).get('team')

      if (!this.selectedTeamId) {
        this.innerHTML =
          '<p class="shnc-empty">Choose a team above to see its ladder.</p>'
        return
      }

      this.innerHTML = '<p class="shnc-loading">Loading the ladder&hellip;</p>'
      try {
        const { teams } = await shncFetch(base, path.teams())
        const team = teams.find((t) => t.id === this.selectedTeamId)
        if (!team) {
          this.innerHTML = '<p class="shnc-empty">That team could not be found.</p>'
          this.reveal()
          return
        }
        gradeId = team.gradeId
        heading = `${tidyName(team.name)} &mdash; ${escapeHtml(team.gradeName || '')}`
      } catch {
        renderError(this, 'The ladder could not be loaded.')
        this.reveal()
        return
      }
    }

    this.innerHTML = '<p class="shnc-loading">Loading the ladder&hellip;</p>'
    try {
      const ladder = await shncFetch(base, path.grade(gradeId))
      const title = heading ? `<h2 class="shnc-heading">${heading}</h2>` : ''

      // Junior grades are published without a ladder: the API returns 200 with
      // an empty list. That is normal, not a failure.
      if (!ladder.ladders.length) {
        this.innerHTML = title +
          '<p class="shnc-empty">No ladder is published for this grade.</p>'
        this.reveal()
        return
      }
      this.innerHTML = title + ladder.ladders.map((l) => this.renderOne(l)).join('')
      this.wireExpanders()
      this.reveal()
    } catch {
      renderError(this, 'The ladder could not be loaded.')
      this.reveal()
    }
  }

  // Picking a team from the list above is a fresh page load, so the browser
  // lands at the top and the whole team grid sits between the visitor and what
  // they asked for — worst on a phone, where that is most of a screen each.
  // Bring the ladder into view instead, and put keyboard and screen reader
  // focus on it so it is not only a visual move.
  //
  // Only when the team came from the URL: a section pinned to one grade in the
  // theme editor has had no click and must not move the page.
  //
  // Called after rendering rather than on a link fragment because the teams
  // list above is fetched too, so the ladder's position is not settled until
  // that grid has drawn. The ladder always finishes last — it waits on the
  // same team list and then the ladder itself.
  reveal() {
    if (!this.selectedTeamId) return
    this.setAttribute('tabindex', '-1')
    this.scrollIntoView()
    this.focus({ preventScroll: true })
  }

  // Any club team is marked; the one the visitor picked is marked more
  // strongly. A grade can hold two of ours, so this is not an either/or.
  rowClass(team) {
    const classes = []
    if (String(team.name).toLowerCase().includes(this.ourTeamName.toLowerCase())) {
      classes.push('shnc-us')
    }
    if (this.selectedTeamId && team.id === this.selectedTeamId) {
      classes.push('shnc-picked')
    }
    return classes.join(' ')
  }

  renderOne(ladder) {
    const { headers, standings, summary } = ladder
    const poolTitle = ladder.pool
      ? `<h3 class="shnc-heading">${escapeHtml(ladder.pool.name)}</h3>` : ''

    // Columns are tiered rather than trimmed: the ones people read carry full
    // weight, the administrative ones step back. Nothing is removed, because
    // forfeits and adjustments do occasionally carry a real number.
    const PRIMARY = ['played', 'won', 'lost', 'drawn']
    const headlineKey = summary.headline?.key
    const tier = headers.map((h) =>
      h.key === headlineKey ? 'shnc-col-pts' : PRIMARY.includes(h.key) ? '' : 'shnc-col-sec')
    const firstSecondary = tier.indexOf('shnc-col-sec')

    const cls = (i) =>
      ['shnc-num', tier[i], i === firstSecondary ? 'shnc-col-div' : ''].filter(Boolean).join(' ')

    const head = '<th class="shnc-num"></th><th>Team</th>' +
      headers.map((h, i) => `<th class="${cls(i)}">${escapeHtml(h.shortName)}</th>`).join('')

    const rows = standings.map((s, i) => {
      const cells = s.values
        .map((v, j) => `<td class="${cls(j)}">${escapeHtml(v)}</td>`)
        .join('')
      return `<tr class="${this.rowClass(s.team)}">` +
        `<td class="shnc-num shnc-pos-cell">${i + 1}</td>` +
        `<td>${escapeHtml(tidyName(s.team.name, true))}</td>${cells}</tr>`
    }).join('')

    const cards = standings.map((s, i) => {
      const headline = summary.headline ? s.values[summary.headline.index] : ''
      const headlineLabel = summary.headline ? summary.headline.shortName : ''
      const sub = summary.columns
        .map((c) => `<span>${escapeHtml(c.shortName)} <b>${escapeHtml(s.values[c.index])}</b></span>`)
        .join('')
      const all = headers
        .map((h, idx) =>
          `<div><span>${escapeHtml(h.name)}</span><span><b>${escapeHtml(s.values[idx])}</b></span></div>`)
        .join('')

      return `<div class="shnc-lcard ${this.rowClass(s.team)}">
        <button class="shnc-lcard-top" aria-expanded="false">
          <span class="shnc-pos">${i + 1}</span>
          <span class="shnc-nm">${escapeHtml(tidyName(s.team.name, true))}</span>
          <span class="shnc-ptswrap"><span class="shnc-pts">${escapeHtml(headline)}</span>
          <span class="shnc-ptslab">${escapeHtml(headlineLabel)}</span></span>
        </button>
        <div class="shnc-sub">${sub}</div>
        <div class="shnc-exp" hidden>${all}</div>
      </div>`
    }).join('')

    return poolTitle +
      `<div class="shnc-scroll shnc-wide"><table class="shnc-table"><thead><tr>${head}</tr></thead>` +
      `<tbody>${rows}</tbody></table></div><div class="shnc-narrow">${cards}</div>`
  }

  wireExpanders() {
    for (const button of this.querySelectorAll('.shnc-lcard-top')) {
      button.addEventListener('click', () => {
        const panel = button.parentElement.querySelector('.shnc-exp')
        const open = !panel.hidden
        panel.hidden = open
        button.setAttribute('aria-expanded', String(!open))
      })
    }
  }
}

customElements.define('shnc-round', ShncRound)
customElements.define('shnc-teams', ShncTeams)
customElements.define('shnc-team', ShncTeam)
customElements.define('shnc-ladder', ShncLadder)
